/**
 * Route-loader query options for the workspace.
 *
 * These replace the per-page `getServer*` helpers from `session.server.ts`.
 * Each one existed to render a page without a loading flash by fetching on the
 * server; a route loader awaiting the same request achieves that, with one
 * important improvement: the data lands in React Query under a key the page's
 * own components already read, so there is a single fetch path per resource
 * instead of a server copy and a client copy that can drift.
 *
 * The `null`-on-failure contract of the originals is kept everywhere it was
 * load-bearing — a page that showed dashes or an empty state when the backend
 * was unreachable still does, rather than throwing to an error boundary.
 */

import { getAlertCounts } from "@/core/lib/alerts-api";
import { apiFetch, apiUrl } from "@/core/lib/api";
import type { StoredDocument } from "@/core/lib/document-store";
import { fetchDocument } from "@/core/lib/documents-api";
import type { EmployeeImportBatch } from "@/core/lib/employee-api";
import { getEmployee } from "@/core/lib/employee-api";
import type { GeneratorTool } from "@/core/lib/generate-api";
import { getPolicy } from "@/core/lib/policy-api";
import type { CompanyData, ProfileData } from "@/core/schema/profile-schema";
import { getSignInMethods } from "@/lib/account-api";
import { getProfile } from "@/lib/profile-api";

export { companyQuery } from "@/lib/workspace-data";

/** Swallows a rejection into `null`, matching the old server helpers. */
async function orNull<T>(p: Promise<T>, label: string): Promise<T | null> {
  try {
    return await p;
  } catch (err) {
    console.error(`[${label}] request failed`, err);
    return null;
  }
}

/** The owner's document-generation profile — prefills the settings forms. */
export const profileQuery = {
  queryKey: ["profile"] as const,
  queryFn: (): Promise<ProfileData | null> => orNull(getProfile(), "profile"),
};

/** Linked sign-in providers — decides "Set password" vs "Change password". */
export const signInMethodsQuery = {
  queryKey: ["sign-in-methods"] as const,
  queryFn: () => orNull(getSignInMethods(), "account"),
};

/**
 * Alert counts by state — the Alerts page header and the dashboard KPI row read
 * this one entry, so a refresh on either page is seen by both. `null` when the
 * backend is unreachable; both callers fall back to zeros.
 */
export const alertCountsQuery = {
  queryKey: ["alerts", "counts"] as const,
  queryFn: () => orNull(getAlertCounts(), "alerts"),
};

/**
 * A single employee by id. `null` when missing, not owned by the caller, or the
 * backend is unreachable — the page renders its not-found state for all three,
 * exactly as the server version did.
 */
export const employeeQuery = (id: string) => ({
  queryKey: ["employee", id] as const,
  queryFn: () => orNull(getEmployee(id), "employees"),
});

/**
 * A single saved document by id.
 *
 * Deliberately does NOT collapse failures to `null`: the route maps `null` to
 * a not-found screen, and the post-generate redirect lands here inside a burst
 * of concurrent requests — a hiccup misreported as a 404 told users their
 * brand-new document was gone. A 404 is `null`; anything else throws so the
 * error boundary can offer a retry. This mirrors the retry-then-throw
 * behaviour of the original `getServerDocument`.
 */
export const documentQuery = (id: string) => ({
  queryKey: ["document", id] as const,
  retry: 1,
  queryFn: async (): Promise<StoredDocument | null> => {
    try {
      return await fetchDocument(id);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) return null;
      throw err;
    }
  },
});

/** The workspace's still-running employee import, if any. */
export const activeEmployeeImportQuery = {
  queryKey: ["employee-import", "active"] as const,
  queryFn: async (): Promise<EmployeeImportBatch | null> => {
    try {
      const res = await apiFetch(apiUrl("/api/employees/import/active"), {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { batch: EmployeeImportBatch | null };
      return data.batch ?? null;
    } catch (err) {
      console.error("[employee-import] backend unreachable", err);
      return null;
    }
  },
};

/**
 * The generator tool config (field set, layout, long-form outline) for a type.
 * Decides whether the route renders the letter wizard or the long-form build
 * flow. `null` when the tool isn't available (unknown id, unpublished tool).
 */
export const generatorToolQuery = (id: string) => ({
  queryKey: ["generator-tool", id] as const,
  queryFn: async (): Promise<GeneratorTool | null> => {
    try {
      const res = await apiFetch(apiUrl(`/api/generate/tool/${id}`), {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) return null;
      return (await res.json()) as GeneratorTool;
    } catch (err) {
      console.error("[generator-tool] backend unreachable", err);
      return null;
    }
  },
});

/** Resolves a non-built-in `type` param to a live admin-created custom tool. */
export const customToolQuery = (id: string) => ({
  queryKey: ["custom-tool", id] as const,
  queryFn: async (): Promise<{
    id: string;
    name: string;
    purpose: string;
  } | null> => {
    try {
      const res = await apiFetch(apiUrl("/api/generate/catalog"), {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        items: { id: string; name: string; purpose: string }[];
      };
      return data.items.find((t) => t.id === id) ?? null;
    } catch (err) {
      console.error("[generator-catalog] backend unreachable", err);
      return null;
    }
  },
});

/** Credit balances + plan limits. Re-exported so routes have one import site. */
export { accountPlanQuery as creditsQuery } from "@/core/hooks/use-account-plan";

export type { CompanyData };

/**
 * A single policy so `/policies/{id}` can show not-found before rendering
 * chrome. `null` on a missing session, a 404, or an unreachable backend — the
 * route treats all three as not-found rather than showing a broken shell.
 */
export const policyQuery = (id: string) => ({
  queryKey: ["policy", id] as const,
  queryFn: () => orNull(getPolicy(id), "policy"),
});
